import { useCallback, useState, useEffect } from 'react'

import { adminGetPreSignedUrlForUpload, adminProcessReportFiles, deleteNewEiarFile } from "../mggadminutil"
import FileDropzone from 'admin/FileDropzone'
import SelectDocTypeForm from './SelectDocTypeForm'
import ConfirmModal from 'common/ConfirmModal'

import { showLoading } from 'services/util'
import Alert from 'common/notifications/Alert'
import { FilledYellow } from 'css/Buttons'
import iconError from 'images/iconColorinfo.svg'
import iconWarning from 'images/icon-warning.svg'
import success from 'images/TickMark.svg'
import * as colors from 'css/Colors'
import css from '../sass/upload.documents.module.scss'
import confirmModalCss from '../sass/confirm.modal.module.scss'

import * as acrUtil from './acr_util'
import * as ceirUtil from './ceir_util'
import * as eiarUtil from './eiar_util'
import * as newEiarUtil from './new_eiar_util'

const successPrefix = "All files uploaded successfully:"
const reports = {
  NEW_EIAR: {...newEiarUtil},
  EIAR: {...eiarUtil},
  CEIR: {...ceirUtil},
  ACR: {...acrUtil},
}

function UploadDocuments (props) {
  const [selectedFiles, setSelectedFiles] = useState([])
  const [dropzoneKey, setDropzoneKey] = useState(0)
  const [submitError, setSubmitError] = useState('')
  const [submitInfo, setSubmitInfo] = useState({message: "", files: []})
  const [submitSuccess, setSubmitSuccess] = useState('')
  const [reportType, setReportType] = useState('NEW_EIAR')
  const [reportYear, setReportYear] = useState('')
  const [ambiguousFileError, setAmbiguousFileError] = useState(null)
  const [newEiarFileUploadCount, setNewEiarFileUploadCount] = useState(0)

  const clearFileUploadResults = useCallback(() => {
    setSubmitError('')
    setSubmitInfo({message: "", files: []})
    setSubmitSuccess('')
    setAmbiguousFileError(null)
  }, [])

  useEffect(() => {
    if (selectedFiles.length === 0) {
      setSubmitError('')
      setSubmitInfo({message: "", files: []})
    }
    else if (selectedFiles.length > 0) {
      setSubmitSuccess('')
      validateFilenames(selectedFiles, reportType)
    }
  }, [selectedFiles, reportType])

  useEffect(() => {
    setReportYear('')
    setSubmitError('')
    setSubmitInfo({message: "", files: []})
    setSubmitSuccess('')
  }, [reportType])

  useEffect(() => {
    setSelectedFiles([])
    setSubmitError('')
    setSubmitInfo({message: "", files: []})
    setSubmitSuccess('')
    setAmbiguousFileError(null)
    setDropzoneKey(key => key + 1)
  }, [reportYear])

  const validateFilenames = (files, docType) => {
    const errors = [];
    const info = {message: "", files: []};
    files.forEach(file => {
      reports[docType].validateFile(file, errors, info)
    })

    if (errors.length !== 0) {
      setSubmitError(
        <>
          <p>The following filenames were not in the proper format. Please rename the files and try again:</p>
          <ul>
            {errors.map((error, index) => (
              <li key={index}><b>{error.msg}: {error.file.name}</b></li>
            ))}
          </ul>
        </>
      )
      setSubmitInfo({message: "", files: []})
      return true;
    }

    setSubmitError('')
    if (info.message || info.files.length !== 0) {
      setSubmitInfo(info)
    }
    else {
      setSubmitInfo({message: "", files: []})
    }
    return false;
  }

  const doSubmitSuccess = (files) => (
    <>
      <b>{successPrefix}</b>
      <ul>
        {files.map((file, index) => (<li key={index}>{file.name}</li>))}
      </ul>
    </>
  )

  const uploadToS3 = async (file, contentType, presignedUrl) => {
    const fetchData = {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: file
    }

    try {
      const response = await fetch(presignedUrl, fetchData);
      return response;
    }
    catch (e) {
      console.log(e)
    }
  }

  const onClear = async e => {
    setSelectedFiles([])
    setSubmitSuccess('')
    setDropzoneKey(dropzoneKey + 1) // hack because dropzone doesn't expose a way to reset itself
  }

  const onSubmit = async e => {

    e.preventDefault()

    if (reportType === 'NEW_EIAR' && !reportYear) {
      setSubmitError('Please select a report year.')
      return;
    }

    if (validateFilenames(selectedFiles, reportType))
      return;

    if (reportType === 'NEW_EIAR')
      setNewEiarFileUploadCount(count => count + 1)

    showLoading(true)

    const uploadResponses = [];
    const uploadErrors = [];
    for (const file of selectedFiles) {
      const presignedUrlResponse = await adminGetPreSignedUrlForUpload({fileName:encodeURIComponent(file.name), reportType:reportType, contentType:file.type})
      const response = await uploadToS3(file, file.type, presignedUrlResponse.presignedUrl)
      if (response && response.ok && response.status === 200)
        uploadResponses.push(response)
      else
        uploadErrors.push({ fileName: file.name, res: (response || {statusCode:'', statusText:'No response'}) })
    }

    if (uploadErrors.length > 0) {
      setSubmitError(doUploadError(uploadErrors))
      showLoading(false)
      return;
    }

    const processResponse = await adminProcessReportFiles(reportType, reportYear)
    if (processResponse.saveFileSuccess && processResponse.saveFileSuccess.length === selectedFiles.length && processResponse.saveFileError.length === 0) {
      setSubmitSuccess(doSubmitSuccess(selectedFiles))
    }
    else {
      const ambiguityError = processResponse.saveFileError
        && processResponse.saveFileError
          .map(error => error.saveFileResponse && error.saveFileResponse.Error)
          .find(error => error && error.code === 'AMBIGUOUS_FILE_ID')
      if (ambiguityError) {
        setAmbiguousFileError(ambiguityError)
      }
      setSubmitError(doProcessError(processResponse))
      showLoading(false)
      return;
    }

    setDropzoneKey(dropzoneKey + 1) // hack because dropzone doesn't expose a way to reset itself

    showLoading(false)
  }

  const doUploadError = (uploadErrors) => (
    <>
      <p>There was an error uploading some of your files</p>
      <ul>
        {uploadErrors.map((error, index) => (
          <li key={index}>{error.fileName}: {error.res.statusText} {error.res.status} </li>
        ))}
      </ul>
    </>
  )

  const doProcessError = (processResponse) => (
    <>
      <p>There was an error processing some of your files</p>

      {!processResponse.saveFileSuccess &&
        <div>Debug info: {JSON.stringify(processResponse,0,2)}</div>}

      {processResponse.saveFileError && processResponse.saveFileError.length > 0 &&
        <ul>
          {processResponse.saveFileError.map((error, index) => (
            <>
              {error.saveFileResponse && error.saveFileResponse.Error &&
                <li key={index}><b>{error.filename}</b> - file Save to DB Error: {error.saveFileResponse.Error.message || error.saveFileResponse.Error}</li>}

              {error.processFileResponse && error.processFileResponse.Error &&
                <li key={index*-1}><b>{error.filename}</b> - file Process to S3 Error: {error.processFileResponse.Error.message}</li>}
            </>
          ))}
        </ul>}

      {processResponse.saveFileSuccess && (!processResponse.saveFileError || processResponse.saveFileError.length === 0) &&
        <div>File Save Debug Info: {JSON.stringify(processResponse.saveFileSuccess,0,2)}</div>}
    </>
  )

  const deleteAmbiguousExistingFile = async () => {
    const existingFileName = ambiguousFileError.existingFileNames[0]
    const response = await deleteNewEiarFile(reportYear, existingFileName)
    if (response && response.success) {
      setSubmitError(`Existing file ${existingFileName} was deleted. Select the replacement file and upload it again.`)
      setSelectedFiles([])
      setDropzoneKey(key => key + 1)
    }
    else {
      setSubmitError(`There was an error deleting ${existingFileName}.`)
    }
    setAmbiguousFileError(null)
  }

  const SelectedReportContent = reports[reportType].ReportContent

  return (
    <div className={css.formStyles}>

      <div className={css.formWrapper}>
        <SelectDocTypeForm onChange={setReportType} />
      </div>

      <h3>Upload {reports[reportType].uploadTitle || reports[reportType].fullName}</h3>

      <SelectedReportContent
        key={reportType}
        reportYear={reportYear}
        setReportYear={setReportYear}
        newEiarFileUploadCount={newEiarFileUploadCount}
        clearFileUploadResults={clearFileUploadResults}
      />

      {submitSuccess &&
        <Alert
          nonPromoPage
          icon={success}
          color={colors.guideGreen}
          width="20" height="15" top="40%" >
          <div>{submitSuccess}</div>
        </Alert>
      }

      <form onSubmit={onSubmit}>

        {(reportType !== 'NEW_EIAR' || reportYear) &&
          <FileDropzone
            key={'dropzone-' + dropzoneKey}
            dropzoneText="Drag and drop Report Files here"
            setSelectedFiles={setSelectedFiles}
          />
        }

        {submitError &&
          <Alert icon={iconError} >
            <div>{submitError}</div>
          </Alert>
        }

        {(submitInfo.message || submitInfo.files.length !== 0) &&
          <Alert icon={iconWarning} color={colors.darkGold}>
            <div>
              <p>{submitInfo.message}</p>
              <ul>
                {submitInfo.files.map((file, index) => (
                  <li key={index}>
                    <b>{file.filename}</b>{file.message && ` - ${file.message}`}
                  </li>
                ))}
              </ul>
            </div>
          </Alert>
        }

        {(reportType !== 'NEW_EIAR' || reportYear) &&
          <>
            <FilledYellow type="submit" disabled={selectedFiles.length === 0 || submitError}>
              {reportType === 'NEW_EIAR' ? 'Upload Files' : 'Upload'}
            </FilledYellow>
            <FilledYellow onClick={onClear} disabled={selectedFiles.length === 0}> Clear </FilledYellow>
          </>
        }
      </form>

      {ambiguousFileError &&
        <ConfirmModal
          onConfirm={deleteAmbiguousExistingFile}
          onCancel={() => setAmbiguousFileError(null)}
          cancelText={"CANCEL"}
          confirmText={"DELETE EXISTING FILE"}
        >
          <div className={confirmModalCss.modalInner}>
            <div className={confirmModalCss.modalText}>
              <p>
                <b>{ambiguousFileError.incomingFileName}</b> was rejected because File ID{' '}
                <b>{ambiguousFileError.fileId}</b> already matches an uploaded file.
              </p>
              <p>Existing file to delete: <b>{ambiguousFileError.existingFileNames[0]}</b></p>
              <p>The rejected file must be selected and uploaded again after deletion.</p>
            </div>
          </div>
        </ConfirmModal>
      }

    </div>
  )
}

export default UploadDocuments;
